// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { BrowserUtils } from '@shared/utils/BrowserUtils';
// ------------
export class LinearGradientBrush {
    constructor(x1, y1, x2, y2, colorStart, colorEnd) {
        this.x1 = x1;
        this.y1 = y1;
        this.x2 = x2;
        this.y2 = y2;
        this.ColorStart = colorStart;
        this.ColorEnd = colorEnd;
    }
    applySettings(ctx) {
        const grd = ctx.createLinearGradient(this.x1, this.y1, this.x2, this.y2);
        grd.addColorStop(0, this.ColorStart);
        grd.addColorStop(1, this.ColorEnd);
        ctx.fillStyle = grd;
    }
}
// ------------
export class SolidBrush {
    constructor(color) {
        this.Color = color;
    }
    applySettings(ctx) {
        ctx.fillStyle = this.Color;
    }
}
// ------------
export class Pen {
    constructor(color, width, dashStyle, lineCap) {
        this.Color = color;
        this.Width = width !== null && width !== void 0 ? width : 1;
        this.DashStyle = dashStyle !== null && dashStyle !== void 0 ? dashStyle : PenStyle.SimpleChart;
        this.LineCap = lineCap !== null && lineCap !== void 0 ? lineCap : '';
    }
    applySettings(ctx) {
        var _a;
        ctx.strokeStyle = (_a = this.Color.Color) !== null && _a !== void 0 ? _a : this.Color;
        ctx.lineWidth = this.Width;
        ctx.setLineDash(this.getLineDash());
        ctx.lineCap = this.LineCap;
    }
    copy() {
        return new Pen(this.Color, this.Width, this.DashStyle, '');
    }
    // #region Process Pen
    setNewProperties(properties) {
        this.Color = properties.Color;
        this.Width = properties.Width;
        this.DashStyle = properties.Style;
    }
    getLineDash() {
        let lineDash = [];
        switch (this.DashStyle) {
            // line
            case PenStyle.SimpleChart:
                lineDash = [];
                break;
            // Dots
            case PenStyle.DotChart:
                lineDash = [1, 1];
                break;
            // Dotted line
            case PenStyle.DotLineChart:
                lineDash = [2, 2, 10, 2];
                break;
            // Iso dot
            case PenStyle.IsoDotChart:
                lineDash = [2, 4];
                break;
            // Shaped line
            case PenStyle.ShapedChart:
                lineDash = [4, 2];
                break;
            // Dotted line
            case PenStyle.ShapedDotChart:
                lineDash = [2, 4, 7, 4];
                break;
            // Dash
            case PenStyle.ShapedDoubleDotChart:
                lineDash = [4, 5];
                break;
            case PenStyle.DragLine:
                lineDash = [3.5, 1.5];
                break;
            case PenStyle.EditLine:
                lineDash = [2, 4];
                break;
            default:
                lineDash = [];
                break;
        }
        const width = this.Width;
        const len = lineDash.length;
        for (let i = 0; i < len; i++) {
            lineDash[i] *= width;
        }
        return lineDash;
    }
}
export var PenStyle;
(function (PenStyle) {
    PenStyle[PenStyle["SimpleChart"] = 0] = "SimpleChart";
    PenStyle[PenStyle["DotChart"] = 1] = "DotChart";
    PenStyle[PenStyle["DotLineChart"] = 2] = "DotLineChart";
    PenStyle[PenStyle["IsoDotChart"] = 3] = "IsoDotChart";
    PenStyle[PenStyle["ShapedChart"] = 4] = "ShapedChart";
    PenStyle[PenStyle["ShapedDotChart"] = 5] = "ShapedDotChart";
    PenStyle[PenStyle["ShapedDoubleDotChart"] = 6] = "ShapedDoubleDotChart";
    PenStyle[PenStyle["HistogrammChart"] = 7] = "HistogrammChart";
    PenStyle[PenStyle["ArrowChart"] = 8] = "ArrowChart";
    PenStyle[PenStyle["HorisontalLineChart"] = 9] = "HorisontalLineChart";
    PenStyle[PenStyle["Ladder"] = 10] = "Ladder";
    PenStyle[PenStyle["DragLine"] = 11] = "DragLine";
    PenStyle[PenStyle["EditLine"] = 12] = "EditLine";
})(PenStyle || (PenStyle = {}));
// ------------
export class Font {
    constructor(height, family, fontWeight = undefined) {
        this.Height = height;
        this.Family = family;
        this.FontWeight = fontWeight;
    }
    toString() {
        let str = `${this.Height}px ${this.Family}`;
        if (this.FontWeight) {
            if (BrowserUtils.isSafari) {
                str = `${str} ${this.FontWeight}`;
            }
            else {
                str = `${this.FontWeight} ${str}`;
            }
        }
        return str;
    }
    copy() {
        return new Font(this.Height, this.Family, this.FontWeight);
    }
    applySettings(ctx) {
        ctx.font = this.toString();
    }
    getCorrectHeight() {
        return this.Height + 2;
    }
}
export class ShadowBrush {
    constructor(color, blur, offsetX, offsetY) {
        this.Color = color;
        this.Blur = blur;
        this.OffsetX = offsetX;
        this.OffsetY = offsetY;
    }
    applySettings(ctx) {
        ctx.shadowBlur = this.Blur;
        ctx.shadowColor = this.Color;
        ctx.shadowOffsetX = this.OffsetX;
        ctx.shadowOffsetY = this.OffsetY;
    }
}
export class PolyLine {
    constructor() {
        this.lines = [];
    }
}
export class PolyRect {
    constructor() {
        this.rects = [];
    }
}
export class Line {
    constructor(x1, y1, x2, y2) {
        this.X1 = x1;
        this.Y1 = y1;
        this.X2 = x2;
        this.Y2 = y2;
    }
}
export class PolyText {
    constructor() {
        this.textArray = [];
    }
}
export class PolyTextItem {
    constructor(text, x, y) {
        this.Text = text;
        this.X = x;
        this.Y = y;
    }
}
// #endregion
// ------------
// #region Graphics
export class Graphics {
    // For creating images etc.
    static getAdditionalCanvas() {
        if (!Graphics.additionalCanvas) {
            const canvas = document.createElement('canvas');
            Graphics.additionalCanvas = canvas;
        }
        return Graphics.additionalCanvas;
    }
    static getCalculationCanvas() {
        if (!Graphics.calculationCanvas) {
            const canvas = document.createElement('canvas');
            Graphics.calculationCanvas = canvas;
        }
        return Graphics.calculationCanvas;
    }
    static getPixelColorFromImage(image, x, y) {
        if (!image)
            return '';
        const canvas = Graphics.getAdditionalCanvas();
        canvas.width = image.width;
        canvas.height = image.height;
        const gr = canvas.getContext('2d');
        gr.drawImage(image, 0, 0);
        return gr.getPixelColor(x, y);
    }
    static makeGrayScale(src_image) {
        if (!src_image)
            return null;
        const canvas = Graphics.getAdditionalCanvas();
        canvas.width = src_image.width;
        canvas.height = src_image.height;
        const gr = canvas.getContext('2d');
        gr.drawImage(src_image, 0, 0);
        const imageData = gr.getImageData(0, 0, src_image.width, src_image.height);
        const data = imageData.data;
        const len = data.length;
        for (let i = 0; i < len; i += 4) {
            const brightness = 0.34 * data[i] + 0.5 * data[i + 1] + 0.16 * data[i + 2];
            // red
            data[i] = brightness;
            // green
            data[i + 1] = brightness;
            // blue
            data[i + 2] = brightness;
        }
        // overwrite original image
        gr.putImageData(imageData, 0, 0);
        return gr.ToImage();
    }
    static createImageWithAlpha(image, alpha) {
        if (!image)
            return null;
        const canvas = Graphics.getAdditionalCanvas();
        const gr = canvas.getContext('2d');
        const oldAlpha = gr.globalAlpha;
        canvas.width = image.width;
        canvas.height = image.height;
        gr.globalAlpha = alpha;
        gr.drawImage(image, 0, 0);
        const alphaImage = gr.ToImage();
        gr.globalAlpha = oldAlpha;
        return alphaImage;
    }
    static GetContrastColor(color, twoColors = true) {
        const channels = Color.getColorChannels(color);
        let r = channels.r;
        let g = channels.g;
        let b = channels.b;
        // Counting the perceptive luminance - human eye favors green color...
        const luminance = 1 - (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        const coef = 0.33;
        if (luminance < 0.5) {
            // bright colors - black font
            if (twoColors)
                return 'rgb(0, 0, 0)';
            r = Graphics.GetContrastValue(r, 1 - coef);
            g = Graphics.GetContrastValue(g, 1 - coef);
            b = Graphics.GetContrastValue(b, 1 - coef);
        }
        else {
            // dark colors - white font
            if (twoColors)
                return 'rgb(255, 255, 255)';
            r = Graphics.GetContrastValue(r, 1 + coef);
            g = Graphics.GetContrastValue(g, 1 + coef);
            b = Graphics.GetContrastValue(b, 1 + coef);
        }
        return Color.CreateColor(Math.floor(r), Math.floor(g), Math.floor(b));
    }
    static GetContrastValue(i, coef) {
        // if (i < min) i = minValue;
        // else if (i > max) i = maxValue;
        // else i *= coef;
        return i;
    }
}
// #endregion
// ------------
// #region RectangleEdgeFilter
class _RectangleEdgeFilter {
    constructor() {
        this.None = 0;
        this.TopLeft = 1;
        this.TopRight = 2;
        this.BottomLeft = 4;
        this.BottomRight = 8;
        this.All = this.TopLeft | this.TopRight | this.BottomLeft | this.BottomRight;
    }
    IsTopLeft(filter) {
        return (this.TopLeft & filter) === this.TopLeft;
    }
    IsTopRight(filter) {
        return (this.TopRight & filter) === this.TopRight;
    }
    IsBottomRight(filter) {
        return (this.BottomRight & filter) === this.BottomRight;
    }
    IsBottomLeft(filter) {
        return (this.BottomLeft & filter) === this.BottomLeft;
    }
}
export const RectangleEdgeFilter = new _RectangleEdgeFilter();
// #endregion
// ------------
// #region Color
export class Rgba {
}
export class _Color {
    constructor() {
        this.Empty = null;
        // Color.Gray = 'gray';
        // Color.Violet = '#EE82EE';
        // Color.Wheat = '#F5DEB3';
        // Color.Red = '#FF0000';
        this.Transparent = '#FFFFFF';
        this.AliceBlue = '#F0F8FF';
        this.AntiqueWhite = '#FAEBD7';
        this.Aqua = '#00FFFF';
        this.Aquamarine = '#7FFFD4';
        this.Azure = '#F0FFFF';
        this.Beige = '#F5F5DC';
        this.Bisque = '#FFE4C4';
        this.Black = '#000000';
        this.BlanchedAlmond = '#FFEBCD';
        this.Blue = '#0000FF';
        this.BlueViolet = '#8A2BE2';
        this.Brown = '#A52A2A';
        this.BurlyWood = '#DEB887';
        this.CadetBlue = '#5F9EA0';
        this.Chartreuse = '#7FFF00';
        this.Chocolate = '#D2691E';
        this.Coral = '#FF7F50';
        this.CornflowerBlue = '#6495ED';
        this.Cornsilk = '#FFF8DC';
        this.Crimson = '#DC143C';
        this.Cyan = '#00FFFF';
        this.DarkBlue = '#00008B';
        this.DarkCyan = '#008B8B';
        this.DarkGoldenrod = '#B8860B';
        this.DarkGray = '#A9A9A9';
        this.DarkGreen = '#006400';
        this.DarkKhaki = '#BDB76B';
        this.DarkMagenta = '#8B008B';
        this.DarkOliveGreen = '#556B2F';
        this.DarkOrange = '#FF8C00';
        this.DarkOrchid = '#9932CC';
        this.DarkRed = '#8B0000';
        this.DarkSalmon = '#E9967A';
        this.DarkSeaGreen = '#8FBC8B';
        this.DarkSlateBlue = '#483D8B';
        this.DarkSlateGray = '#2F4F4F';
        this.DarkTurquoise = '#00CED1';
        this.DarkViolet = '#9400D3';
        this.DeepPink = '#FF1493';
        this.DeepSkyBlue = '#00BFFF';
        this.DimGray = '#696969';
        this.DodgerBlue = '#1E90FF';
        this.Firebrick = '#B22222';
        this.FloralWhite = '#FFFAF0';
        this.ForestGreen = '#228B22';
        this.Fuchsia = '#FF00FF';
        this.Gainsboro = '#DCDCDC';
        this.GhostWhite = '#F8F8FF';
        this.Gold = '#FFD700';
        this.Goldenrod = '#DAA520';
        this.Gray = '#808080';
        this.Green = '#008000';
        this.GreenYellow = '#ADFF2F';
        this.Honeydew = '#F0FFF0';
        this.HotPink = '#FF69B4';
        this.IndianRed = '#CD5C5C';
        this.Indigo = '#4B0082';
        this.Ivory = '#FFFFF0';
        this.Khaki = '#F0E68C';
        this.Lavender = '#E6E6FA';
        this.LavenderBlush = '#FFF0F5';
        this.LawnGreen = '#7CFC00';
        this.LemonChiffon = '#FFFACD';
        this.LightBlue = '#ADD8E6';
        this.LightCoral = '#F08080';
        this.LightCyan = '#E0FFFF';
        this.LightGoldenrodYellow = '#FAFAD2';
        this.LightGreen = '#90EE90';
        this.LightGray = '#D3D3D3';
        this.LightPink = '#FFB6C1';
        this.LightSalmon = '#FFA07A';
        this.LightSeaGreen = '#20B2AA';
        this.LightSkyBlue = '#87CEFA';
        this.LightSlateGray = '#778899';
        this.LightSteelBlue = '#B0C4DE';
        this.LightYellow = '#FFFFE0';
        this.Lime = '#00FF00';
        this.LimeGreen = '#32CD32';
        this.Linen = '#FAF0E6';
        this.Magenta = '#FF00FF';
        this.Maroon = '#800000';
        this.MediumAquamarine = '#66CDAA';
        this.MediumBlue = '#0000CD';
        this.MediumOrchid = '#BA55D3';
        this.MediumPurple = '#9370DB';
        this.MediumSeaGreen = '#3CB371';
        this.MediumSlateBlue = '#7B68EE';
        this.MediumSpringGreen = '#00FA9A';
        this.MediumTurquoise = '#48D1CC';
        this.MediumVioletRed = '#C71585';
        this.MidnightBlue = '#191970';
        this.MintCream = '#F5FFFA';
        this.MistyRose = '#FFE4E1';
        this.Moccasin = '#FFE4B5';
        this.NavajoWhite = '#FFDEAD';
        this.Navy = '#000080';
        this.OldLace = '#FDF5E6';
        this.Olive = '#808000';
        this.OliveDrab = '#6B8E23';
        this.Orange = '#FFA500';
        this.Orange2 = '#FF6600';
        this.OrangeRed = '#FF4500';
        this.Orchid = '#DA70D6';
        this.PaleGoldenrod = '#EEE8AA';
        this.PaleGreen = '#98FB98';
        this.PaleTurquoise = '#AFEEEE';
        this.PaleVioletRed = '#DB7093';
        this.PapayaWhip = '#FFEFD5';
        this.PeachPuff = '#FFDAB9';
        this.Peru = '#CD853F';
        this.Pink = '#FFC0CB';
        this.Plum = '#DDA0DD';
        this.PowderBlue = '#B0E0E6';
        this.Purple = '#800080';
        this.Red = '#FF0000';
        this.RosyBrown = '#BC8F8F';
        this.RoyalBlue = '#4169E1';
        this.SaddleBrown = '#8B4513';
        this.Salmon = '#FA8072';
        this.SandyBrown = '#F4A460';
        this.SeaGreen = '#2E8B57';
        this.SeaShell = '#FFF5EE';
        this.Sienna = '#A0522D';
        this.Silver = '#C0C0C0';
        this.SkyBlue = '#87CEEB';
        this.SlateBlue = '#6A5ACD';
        this.SlateGray = '#708090';
        this.Snow = '#FFFAFA';
        this.SpringGreen = '#00FF7F';
        this.SteelBlue = '#4682B4';
        this.Tan = '#D2B48C';
        this.Teal = '#008080';
        this.Thistle = '#D8BFD8';
        this.Tomato = '#FF6347';
        this.Turquoise = '#40E0D0';
        this.Violet = '#EE82EE';
        this.Wheat = '#F5DEB3';
        this.White = '#FFFFFF';
        this.WhiteSmoke = '#F5F5F5';
        this.Yellow = '#FFFF00';
        this.YellowGreen = '#9ACD32';
    }
    // alpha: 0 - 255. For compatibility with C#.
    // colorString: rgb/rgba formats.
    FromArgb(alpha, colorString) {
        const channels = Color.getColorChannels(colorString);
        const a = alpha / 255;
        return 'rgba(' + channels.r + ',' + channels.g + ',' + channels.b + ',' + a + ')';
    }
    FromHexToArgb(alpha, colorString) {
        let _colorString;
        if (this.IsHexColor(colorString)) {
            _colorString = this.HexToRgb(colorString);
        }
        else {
            _colorString = this.getColorChannels(colorString);
        }
        const a = alpha / 255;
        return 'rgba(' + _colorString.r + ',' + _colorString.g + ',' + _colorString.b + ',' + a + ')';
    }
    IsHexColor(hex) {
        return /^#([0-9A-F]{3}|[0-9A-F]{4}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(hex);
    }
    HexToRgb(hex) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
            ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16)
            }
            : null;
    }
    rgbaToHex(rgbaString) {
        const rgbaArray = rgbaString
            .substring(5, rgbaString.length - 1)
            .split(',')
            .map(Number);
        const [r, g, b, a] = rgbaArray;
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}${Math.round(a * 255)
            .toString(16)
            .padStart(2, '0')}`;
    }
    // For rgb/rgba schemes only.
    // minAlpha: 0 - 1.
    GetLighterColor(min, max, value, colorString, minAlpha) {
        const overall = max - min;
        const correctionFactor = overall === 0 ? 1 : (value - min) / overall;
        const channels = this.getColorChannels(colorString);
        const srcA = channels.hasOwnProperty('a') ? channels.a : 1;
        let alpha = srcA * correctionFactor;
        if (alpha > 1)
            alpha = 1;
        else if (alpha < minAlpha)
            alpha = minAlpha;
        return ('rgba(' +
            Math.round(channels.r) +
            ',' +
            Math.round(channels.g) +
            ',' +
            Math.round(channels.b) +
            ',' +
            alpha +
            ')');
    }
    // For rgb/rgba schemes only.
    getColorChannels(colorString) {
        const rgba = new Rgba();
        if (this.IsHexColor(colorString)) {
            let hex = colorString.replace('#', '');
            if (hex.length === 3) {
                hex = hex
                    .split('')
                    .map((c) => c + c)
                    .join('');
                colorString = `#${hex}`;
            }
            const channels = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(colorString).slice(1);
            rgba.r = parseInt(channels[0], 16);
            rgba.g = parseInt(channels[1], 16);
            rgba.b = parseInt(channels[2], 16);
            if (channels.length > 3) {
                rgba.a = parseInt(channels[3], 16);
            }
        }
        else {
            const channels = colorString.slice(colorString.indexOf('(') + 1).split(',');
            rgba.r = parseInt(channels[0]);
            rgba.g = parseInt(channels[1]);
            rgba.b = parseInt(channels[2]);
            if (channels.length > 3) {
                rgba.a = parseFloat(channels[3]);
            }
        }
        return rgba;
    }
    // a - alpha (0 - 1).
    CreateColor(r, g, b, a = undefined) {
        let color = a === undefined || a === 1 ? 'rgb(' : 'rgba(';
        color += r + ',' + g + ',' + b;
        if (a !== undefined && a !== 1) {
            color += ',' + a;
        }
        color += ')';
        return color;
    }
}
export const Color = new _Color();
// #endregion
// #region Brushes
class _Brushes {
    constructor() {
        this.White = new SolidBrush('white');
        this.Gray = new SolidBrush('gray');
        this.Red = new SolidBrush('red');
        this.Green = new SolidBrush('green');
        this.Black = new SolidBrush(Color.Black);
    }
}
export const Brushes = new _Brushes();
// #endregion
class _Pens {
    constructor() {
        this.White = new Pen('white');
        this.Transparent = new Pen('transparent');
        this.Black = new Pen(Color.Black);
    }
}
export const Pens = new _Pens();
